import EmblaCarousel from 'embla-carousel';
import AutoScroll from 'embla-carousel-auto-scroll';
import Player from '@vimeo/player';
import { toggleAriaExpanded, FocusTrap } from './utils';
import { gsap } from 'gsap';


// Header mobile flyout

const headerMobileToggle = document.querySelector('.header-mobile-toggle');
const headerMobileFlyout = document.querySelector('.header-mobile-flyout');
const headerMobileMenu = headerMobileFlyout.querySelector('.header-menu');
const headerMobileMegamenuToggles = headerMobileMenu.querySelectorAll('.header-megamenu-toggle');
const headerMobileMegamenuBackButtons = headerMobileMenu.querySelectorAll('.header-megamenu-back');

const headerFlyoutAnimation = gsap
    .timeline({ paused: true, defaults: { duration: 0.3, ease: 'power3.inOut' } })
    .set(headerMobileFlyout, { yPercent: 10 })
    .to(headerMobileFlyout, { yPercent: 0, autoAlpha: 1 });

function toggleFlyout(expand) {
    document.body.classList.toggle('flyout-is-open', expand);
    toggleAriaExpanded(headerMobileToggle, expand);

    if (expand) {
        headerFlyoutAnimation.play();
    } else {
        headerFlyoutAnimation.reverse();
    }
}

headerMobileToggle.addEventListener('click', () => {
    toggleFlyout(!document.body.classList.contains('flyout-is-open'));
});

function toggleMobileMegamenu(toggle, expand) {
    const megamenu = toggle.nextElementSibling;

    if (expand) {
        gsap.to(headerMobileMenu, { xPercent: -100, duration: 0.3, height: megamenu.clientHeight, ease: 'power3.inOut' });
        gsap.to(megamenu, { autoAlpha: 1 });
    } else {
        gsap.to(headerMobileMenu, { xPercent: 0, duration: 0.3, height: 'auto', ease: 'power3.inOut' });
        gsap.to(megamenu, { autoAlpha: 0 });
    }

    toggleAriaExpanded(toggle, expand);
}

headerMobileMegamenuToggles.forEach(toggle => {
    toggle.addEventListener('click', e => {
        e.preventDefault();
        toggleMobileMegamenu(toggle, true);
    });
});

headerMobileMegamenuBackButtons.forEach(button => {
    button.addEventListener('click', () => {
        const megamenu = button.closest('.header-megamenu-flyout');
        const toggle = megamenu.previousElementSibling;
        toggleMobileMegamenu(toggle, false);
        toggle.focus();
    });
});


// Header desktop megamenus
// On touch devices, we want to prevent the default link behavior allowing users to open the megamenu without navigating away.

const headerMegamenuToggles = document.querySelectorAll('.header-desktop-menu .header-megamenu-toggle');
let lastInputType = null;

headerMegamenuToggles.forEach(toggle => {
    toggle.addEventListener('pointerdown', e => {
        lastInputType = e.pointerType;
    });

    toggle.addEventListener('click', e => {
        if (lastInputType === 'touch') {
            e.preventDefault();
        }
    });
});


// Header search

const headerSearchToggles = document.querySelectorAll('.header-search-toggle');
const headerSearchFlyout = document.querySelector('.header-search-flyout');
const headerSearchInput = headerSearchFlyout.querySelector('.header-search-input');
const headerSearchFocusTrap = new FocusTrap(headerSearchFlyout);

const headerSearchAnimation = gsap
    .timeline({ paused: true, defaults: { duration: 0.3, ease: 'power3.inOut' } })
    .set(headerSearchFlyout, { yPercent: -10 })
    .to(headerSearchFlyout, { yPercent: 0, autoAlpha: 1 });

let lastUsedSearchToggle;

function toggleSearch(expand) {
    document.body.classList.toggle('search-is-open', expand);

    headerSearchToggles.forEach(toggle => {
        toggleAriaExpanded(toggle, expand);
    });

    if (expand) {
        headerSearchAnimation.play().then(() => {
            headerSearchFocusTrap.enable();
            headerSearchInput.focus();
        });
    } else {
        headerSearchAnimation.reverse().then(() => {
            headerSearchFocusTrap.disable();
            lastUsedSearchToggle?.focus();
        });
    }
}

headerSearchToggles.forEach(toggle => {
    toggle.addEventListener('click', () => {
        const expand = !document.body.classList.contains('search-is-open');

        toggleSearch(expand);
        
        if (expand) {
            lastUsedSearchToggle = toggle;
        }
    });
});


// Scroll detection class

function handleScroll() {
    document.body.classList.toggle('user-has-scrolled', window.scrollY > 0);
}

window.addEventListener('scroll', handleScroll);
handleScroll();


// Sliders

const sliderBlocks = document.querySelectorAll('[data-slider]');

sliderBlocks.forEach(block => {
    const container = block.querySelector('[data-slider-container]');
    const autoscroll = container?.dataset.autoscroll === 'true';
    const prevButton = block.querySelector('[data-slider-prev]');
    const nextButton = block.querySelector('[data-slider-next]');

    const plugins = [];
    const options = {
        align: 'start',
    };

    if (autoscroll) {
        plugins.push(AutoScroll({
            stopOnInteraction: false,
            stopOnMouseEnter: true,
            speed: 1,
        }));
        options.loop = true;
    }

    const embla = EmblaCarousel(container, options, plugins);

    if (prevButton && nextButton) {
        function updateSlider() {
            prevButton.disabled = !embla.canScrollPrev();
            nextButton.disabled = !embla.canScrollNext();
        }

        window.addEventListener('resize', () => updateSlider());
        embla.on('select', updateSlider);
        updateSlider();

        prevButton.addEventListener('click', () => embla.scrollPrev());
        nextButton.addEventListener('click', () => embla.scrollNext());
    }
});


// Accordion blocks

const accordionBlocks = document.querySelectorAll('.accordion');

accordionBlocks.forEach(block => {
    const items = block.querySelectorAll('.accordion-item');

    items.forEach(item => {
        const toggle = item.querySelector('.accordion-item-toggle');
        const collapse = item.querySelector('.accordion-item-collapse');

        item.animation = gsap
            .timeline({ paused: true, defaults: { duration: 0.3, ease: 'power3.inOut' } })
            .set(collapse, { display: 'block', height: 'auto' })
            .from(collapse, { height: 0 });

        toggle.addEventListener('click', () => {
            const isExpanded = item.classList.contains('open');

            // Close all items
            items.forEach(otherItem => {
                const otherToggle = otherItem.querySelector('.accordion-item-toggle');
                toggleAriaExpanded(otherToggle, false);
                otherItem.classList.remove('open');
                otherItem.animation.reverse();
            });

            // Open clicked item if it was closed
            if (!isExpanded) {
                toggleAriaExpanded(toggle, true);
                item.classList.add('open');
                item.animation.play();
            }
        });
    });
});


// Vimeo background videos

const videoElements = document.querySelectorAll('[data-video-url]');

videoElements.forEach(videoElement => {
    const toggleButton = videoElement.querySelector('[data-video-toggle]');

    const player = new Player(videoElement, {
        url: videoElement.dataset.videoUrl,
        background: true,
    });

    videoElement.classList.add('playing');

    toggleButton?.addEventListener('click', () => {
        player.getPaused().then(paused => {
            videoElement.classList.toggle('playing', paused);
            paused ? player.play() : player.pause();
        });
    });
});


// Tips visibility

const tipsContainer = document.querySelector('.tips');

if (tipsContainer) {
    const tipItems = tipsContainer.querySelectorAll('.tip');

    if (tipItems.length) {
        const STORAGE_KEY = 'closedTips';

        function getClosedTips() {
            try {
                return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
            } catch {
                return [];
            }
        }

        function markTipClosed(tipId) {
            const closed = getClosedTips();
            if (!closed.includes(tipId)) {
                closed.push(tipId);
                localStorage.setItem(STORAGE_KEY, JSON.stringify(closed));
            }
        }

        function showTip(tip) {
            tip.classList.add('visible');
            tipsContainer.classList.add('visible');
        }

        function closeTip(tip) {
            markTipClosed(tip.dataset.tipId);
            tip.classList.remove('visible');

            if (!tipsContainer.querySelector('.tip.visible')) {
                tipsContainer.classList.remove('visible');
            }
        }

        const closedTips = getClosedTips();

        tipItems.forEach(tip => {
            const tipId = tip.dataset.tipId;

            if (closedTips.includes(tipId)) return;

            const triggerType = tip.dataset.triggerType;
            const delay = parseFloat(tip.dataset.delay) || 0;
            const scrollPosition = parseInt(tip.dataset.scrollPosition) || 0;

            if (triggerType === 'direct') {
                showTip(tip);
            } else if (triggerType === 'time') {
                setTimeout(() => showTip(tip), delay * 1000);
            } else if (triggerType === 'scroll') {
                const onScroll = () => {
                    if (window.scrollY >= scrollPosition) {
                        showTip(tip);
                        window.removeEventListener('scroll', onScroll);
                    }
                };
                window.addEventListener('scroll', onScroll, { passive: true });
                onScroll();
            }

            tip.querySelector('.tip-close')?.addEventListener('click', () => closeTip(tip));
        });
    }
}